import { ensureColorContrast, isDarkBackground } from "./mapThemes.js";

const WHEEL_TREATMENTS = Object.freeze({
  // A map theme can share its color language with wheels while using a distinct
  // visual grammar suited to the map's purpose.
  ink: "editorial",
  editorial: "editorial",
  swiss: "editorial",
  graphite: "editorial",
  accessible: "depth",
  "high-contrast": "depth",
  blueprint: "depth",
  signal: "depth",
  systems: "depth",
  underground: "depth",
  tide: "domains",
  botanical: "domains",
  clay: "domains",
  dusk: "domains",
  mineral: "domains",
  solar: "domains",
  glacier: "domains",
  desert: "domains",
  wildflower: "domains",
  tropical: "domains",
  slate: "luminous",
  aurora: "luminous",
  "neon-night": "luminous",
  midnight: "luminous",
  firefly: "luminous",
  arcade: "luminous",
});

export function getFuturesWheelTreatment(theme) {
  return WHEEL_TREATMENTS[theme?.id] || "branches";
}

/** Keep palette accents distinct from the canvas as non-text map marks. */
export function futuresWheelContrastAccent(theme, color, opacity = 1) {
  return ensureColorContrast(color || theme.text || "#303942", theme.background || "#ffffff", 3, opacity);
}

/** Use the same hierarchy colors and palette accents as the Knowledge Map theme. */
export function futuresWheelLevelAppearance(theme, order) {
  const level = Math.max(0, Math.min(5, Number(order) || 0));
  const treatment = getFuturesWheelTreatment(theme);
  const fills = [theme.focalNode, theme.coreLevelNode, theme.node, theme.detailNode, theme.detailNode, theme.detailNode];
  const borders = [theme.focalBorder, theme.coreLevelBorder, theme.nodeBorder, theme.detailBorder, theme.detailBorder, theme.detailBorder];
  const fill = fills[level] || theme.node || "#ffffff";
  const palette = theme.palette || [];
  const depthColor = palette[Math.max(0, level - 1) % Math.max(1, palette.length)] || borders[level] || "#315f90";
  const neutralColor = theme.canvas === "dark" ? theme.text || "#ffffff" : theme.nodeBorder || "#343a40";
  const accent = futuresWheelContrastAccent(theme,
    treatment === "editorial" ? neutralColor
      : treatment === "depth" && level > 0 ? depthColor
        : level === 0 ? theme.focalBorder || palette[0] || "#315f90"
          : depthColor
  );
  return {
    fill,
    border: accent,
    accent,
    text: isDarkBackground(fill) ? "#ffffff" : theme.text || "#252b30",
  };
}

export function futuresWheelEdgeAppearance(theme, order, branchAccent) {
  const treatment = getFuturesWheelTreatment(theme);
  if (treatment === "editorial") {
    return futuresWheelContrastAccent(theme, theme.canvas === "dark" ? theme.text : theme.text || theme.nodeBorder, 0.98);
  }
  if (treatment === "depth") {
    const palette = theme.palette || [];
    return futuresWheelContrastAccent(theme, palette[Math.max(0, Number(order) - 1) % Math.max(1, palette.length)] || theme.text, 0.98);
  }
  return branchAccent;
}

export function futuresWheelSectorAppearance(theme, accent) {
  const treatment = getFuturesWheelTreatment(theme);
  if (treatment === "editorial") {
    const neutral = theme.canvas === "dark" ? theme.text : theme.nodeBorder || theme.text;
    return { accent: futuresWheelContrastAccent(theme, neutral, 0.9), fillOpacity: 0.035, strokeOpacity: 0.32, strokeWidth: 1.5 };
  }
  if (treatment === "depth") {
    return { accent: futuresWheelContrastAccent(theme, theme.nodeBorder || theme.text, 0.78), fillOpacity: 0.025, strokeOpacity: 0.28, strokeWidth: 1.5 };
  }
  if (treatment === "domains") {
    return { accent, fillOpacity: 0.24, strokeOpacity: 1, strokeWidth: 3.5 };
  }
  if (treatment === "luminous") {
    return { accent, fillOpacity: 0.08, strokeOpacity: 0.72, strokeWidth: 2 };
  }
  return { accent, fillOpacity: 0.14, strokeOpacity: 0.8, strokeWidth: 2.25 };
}

export function futuresWheelGuideAppearance(theme) {
  const treatment = getFuturesWheelTreatment(theme);
  if (treatment === "editorial") return { opacity: 0.25, dash: undefined, width: 1 };
  if (treatment === "depth") return { opacity: 0.38, dash: undefined, width: 1.25 };
  if (treatment === "luminous") return { opacity: 0.52, dash: "3 9", width: 1.25 };
  return { opacity: 0.32, dash: "4 8", width: 1.1 };
}
